import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, CalendarRange, Plus } from 'lucide-react';

import { Card } from '../../components/Shared/Card';
import { Loading } from '../../components/Shared/Loading';
import { Button } from '../../components/Shared/Button';
import { EmptyState } from '../../components/Shared/EmptyState';
import { Badge } from '../../components/Shared/Badge';
import { SlideOver } from '../../components/Shared/SlideOver';
import { Modal } from '../../components/Shared/Modal';
import MapPicker from '../../components/Shared/MapPicker';

import { useObjectStore } from '../../store/useObjectStore';
import { selectObjectOffers, useOfferStore } from '../../store/useOfferStore';
import { useCustomerStore } from '../../store/useCustomerStore';
import { useTaskStore } from '../../store/useTaskStore';
import { useServiceStore } from '../../store/useServiceStore';

import { TaskForm } from '../Tasks/components/TaskForm';
import ObjectEditSlideOver from './components/ObjectEditSlideOver';
import { OfferAmountBreakdown } from './components/OfferAmountBreakdown';
import { ObjectTasksCard } from './components/ObjectTasksCard';
import { splitOfferPositions, sumNet } from './components/splitOfferPositions';
import { NoTaskHint } from '../Offers/components/NoTaskHint';
import { AdHocPlanEntrySlideOver } from '../Planning/AdHocPlanEntrySlideOver';
import { StandingInvoiceDialog } from '../Invoices/components/StandingInvoiceDialog';
import { DocumentList } from '../../components/Documents/DocumentList';

import type { UpdateObjectRequestWithGeo } from '../../types/objects-api';
import { isStandingInvoice, objectBillingLabel } from '../../types/billing';
import { objectTypeLabelKey, toObjectType } from '../../types/objectType';
import { formatMoney } from '../../utils/money';
import { useTranslation } from '../../i18n';

export default function ObjectDetailPage() {
  const { t } = useTranslation(['objects', 'common']);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { getObjectById, selectedObject, loading, error, updateObject } = useObjectStore();
  const { customers, fetchCustomers } = useCustomerStore();

  const { fetchObjectTasks } = useTaskStore();
  const { services, fetchServices } = useServiceStore();

  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [tasksInitialLoadComplete, setTasksInitialLoadComplete] = useState(false);

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isCreateTaskOpen, setIsCreateTaskOpen] = useState(false);

  useEffect(() => {
    if (!id) return;

    Promise.all([
      getObjectById(id).then(() => setInitialLoadComplete(true)),
      fetchCustomers({ pageSize: 1000 }),
      fetchServices(),
      fetchObjectTasks(id).then(() => setTasksInitialLoadComplete(true)),
    ]);
  }, [id, getObjectById, fetchCustomers, fetchServices, fetchObjectTasks]);

  useEffect(() => {
    const wantsEdit = searchParams.get('edit') === '1';
    if (wantsEdit) setIsEditOpen(true);
  }, [searchParams]);

  const customerName = useMemo(() => {
    if (!selectedObject?.customerId) return '—';
    return customers.find((c: { id?: string; name?: string | null }) => c.id === selectedObject.customerId)?.name || '—';
  }, [customers, selectedObject?.customerId]);

  const formatAddress = () => {
    const a = selectedObject?.address;
    if (!a) return '—';

    const street = [a.street, (a as unknown as { streetNumber?: string | null }).streetNumber]
      .filter(Boolean)
      .join(' ');
    const cityLine = [a.postalCode, a.city].filter(Boolean).join(' ');

    return [street, cityLine, a.country].filter(Boolean).join(', ') || '—';
  };

  const objectStatusBadge = () => {
    const status = (selectedObject?.status ?? '').toLowerCase();
    if (status === 'inactive') return <Badge variant="default">{t('common:status.inactive')}</Badge>;
    return <Badge variant="success">{t('common:status.active')}</Badge>;
  };

  const openEdit = () => {
    setSearchParams((prev) => {
      prev.set('edit', '1');
      return prev;
    });
    setIsEditOpen(true);
  };

  const formatGeo = (obj: typeof selectedObject) => {
    const lat = obj?.latitude;
    const lon = obj?.longitude;
    if (lat === null || lat === undefined || lon === null || lon === undefined) return '—';
    return `${lat}, ${lon}`;
  };

  const goBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate('/objects');
  };

  if (loading && !initialLoadComplete) {
    return <Loading />;
  }

  if (error && !selectedObject && initialLoadComplete) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <Button
          variant="secondary"
          size="sm"
          onClick={goBack}
          leftIcon={<ArrowLeft className="w-4 h-4" strokeWidth={2} />}
        >
          {t('objects:detail.backButton')}
        </Button>
        <Card>
          <EmptyState
            variant="error"
            title={t('common:errors.loadFailed')}
            description={error}
            onRetry={() => id && getObjectById(id)}
          />
        </Card>
      </div>
    );
  }

  if (!selectedObject) return null;

  const selectedObjectType = toObjectType(selectedObject.type);

  return (
    <div className="space-y-4 sm:space-y-6">
      <Button
        variant="secondary"
        size="sm"
        onClick={goBack}
        leftIcon={<ArrowLeft className="w-4 h-4" strokeWidth={2} />}
      >
        {t('objects:detail.backButton')}
      </Button>

      {/* Header card */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-[22px] sm:text-[24px] font-semibold tracking-tight text-slate-900 truncate">
                {selectedObject.name || t('objects:fields.name')}
              </h1>
              {objectStatusBadge()}
            </div>
            <p className="text-[13px] text-slate-500 mt-0.5 truncate">
              {customerName} · {formatAddress()}
            </p>
          </div>

          <Button onClick={openEdit} className="w-full sm:w-auto flex-shrink-0">
            {t('objects:detail.editButton')}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        <Card title={t('objects:detail.detailsCard')} className="lg:col-span-1">
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-[11px] font-medium uppercase tracking-wider text-slate-500">{t('objects:fields.customer')}</span>
                <span className="text-[13px] font-medium text-slate-900 truncate">{customerName}</span>
              </div>
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-[11px] font-medium uppercase tracking-wider text-slate-500">{t('objects:address.title')}</span>
                <span className="text-[13px] text-slate-700 text-right">{formatAddress()}</span>
              </div>
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-[11px] font-medium uppercase tracking-wider text-slate-500">{t('objects:fields.billingInterval')}</span>
                <span className="text-[13px] font-medium text-slate-900 text-right">
                  {objectBillingLabel(t, selectedObject)}
                </span>
              </div>
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-[11px] font-medium uppercase tracking-wider text-slate-500">{t('objects:fields.type')}</span>
                <span className="text-[13px] font-medium text-slate-900 text-right">
                  {selectedObjectType ? t(objectTypeLabelKey(selectedObjectType)) : '—'}
                </span>
              </div>
            </div>

            <div className="pt-4 border-t border-slate-100">
              <h3 className="text-[13px] font-semibold text-slate-900 mb-1">{t('objects:geolocation.title')}</h3>
              <p className="text-[12px] text-slate-500 mb-3 tabular-nums">
                {formatGeo(selectedObject)}
              </p>

              <MapPicker
                value={
                  selectedObject.latitude == null || selectedObject.longitude == null
                    ? null
                    : { latitude: selectedObject.latitude, longitude: selectedObject.longitude }
                }
                onChange={() => undefined}
                disabled
                heightClassName="h-56"
                containerClassName="ff-map"
              />
            </div>
          </div>
        </Card>

        <div className="lg:col-span-2 space-y-4 sm:space-y-6">
          <ActiveOfferCard objectId={id} object={selectedObject} />

          <ObjectTasksCard
            objectId={id}
            initialLoadComplete={tasksInitialLoadComplete}
            onCreateTask={() => setIsCreateTaskOpen(true)}
            onRetry={() => id && fetchObjectTasks(id)}
          />
        </div>
      </div>

      {id && <DocumentList ownerType="Object" ownerId={id} />}

      {isEditOpen && (
        <ObjectEditSlideOver
          isOpen
          onClose={() => setIsEditOpen(false)}
          mode="edit"
          object={selectedObject}
          onSave={async (payload: UpdateObjectRequestWithGeo) => {
            if (!id) return;
            await updateObject(id, payload);
            await getObjectById(id);
          }}
        />
      )}

      <SlideOver
        isOpen={isCreateTaskOpen}
        onClose={() => setIsCreateTaskOpen(false)}
        title={t('objects:detail.createTaskTitle')}
        size="lg"
      >
        <TaskForm
          onClose={() => {
            setIsCreateTaskOpen(false);
            if (id) fetchObjectTasks(id);
          }}
          objects={[selectedObject]}
          services={services}
        />
      </SlideOver>
    </div>
  );
}

interface ActiveOfferCardProps {
  objectId?: string;
  object: {
    activeOfferId?: string | null;
    activeOfferNumber?: string | null;
    billingInterval?: string | null;
  };
}

const formatCurrencyEUR = (value: number) =>
  value.toLocaleString('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDateDE = (dateStr?: string | null) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

function ActiveOfferCard({ objectId, object }: ActiveOfferCardProps) {
  const { t } = useTranslation(['invoices', 'objects', 'offers']);
  const navigate = useNavigate();
  const [isAdHocOpen, setIsAdHocOpen] = useState(false);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isStandingOpen, setIsStandingOpen] = useState(false);

  const activeOfferId = object.activeOfferId ?? null;
  // why: a standing invoice needs the object billed by standing invoice and an active offer (FAFD-27).
  const canCreateStanding = !!objectId && !!activeOfferId && isStandingInvoice(object.billingInterval);

  const {
    selectedOffer: activeOfferFull,
    getOfferById,
    fetchObjectOffers,
  } = useOfferStore();
  const offers = useOfferStore(selectObjectOffers(objectId));
  const { setActiveOffer, saving } = useObjectStore();

  useEffect(() => {
    if (activeOfferId) {
      void getOfferById(activeOfferId);
    }
  }, [activeOfferId, getOfferById]);

  useEffect(() => {
    if (objectId) {
      void fetchObjectOffers(objectId);
    }
  }, [objectId, fetchObjectOffers]);

  const acceptedOffers = useMemo(
    () => offers.filter((o) => (o.status ?? '').toLowerCase() === 'accepted'),
    [offers],
  );

  // why: both amounts are shown with their positions in a tooltip — the offer total also
  // holds one-off, quarterly and yearly positions (FAFD-15). Grouped by the billing kind the
  // invoice uses, never by interval text (FAFD-27).
  const activePositions = useMemo(
    () => (activeOfferFull?.id === activeOfferId ? activeOfferFull?.positions ?? [] : []),
    [activeOfferFull, activeOfferId],
  );
  const positionSplit = useMemo(() => splitOfferPositions(activePositions), [activePositions]);
  const monthlyPositions = positionSplit.monthly;
  const monthlyTotal = sumNet(monthlyPositions);
  // why: seasonal monthly positions (e.g. winter service Nov–Mar) are not on every month's
  // invoice — shown next to the flat rate, not in it.
  const seasonalTotal = sumNet(positionSplit.seasonal);
  const seasonalGroup = { heading: t('objects:activeOffer.breakdown.seasonal'), positions: positionSplit.seasonal };
  const offerTotalGroups = [
    { heading: t('objects:activeOffer.breakdown.inFlatRate'), positions: positionSplit.monthly },
    seasonalGroup,
    { heading: t('offers:billing.kinds.Quarterly'), positions: positionSplit.quarterly },
    { heading: t('offers:billing.kinds.Yearly'), positions: positionSplit.yearly },
    { heading: t('offers:billing.kinds.OneOff'), positions: positionSplit.oneOff },
    { heading: t('offers:billing.kinds.ByEffort'), positions: positionSplit.byEffort },
    { heading: t('offers:billing.kinds.Unknown'), positions: positionSplit.review },
  ];

  const offerShown = activeOfferFull?.id === activeOfferId ? activeOfferFull : null;
  const switchableOffers = acceptedOffers.filter((o) => o.id !== activeOfferId);
  // why: the fixed amounts of a standing-invoice object are on its standing invoice — tell the
  // user why the monthly run only carries the extras (FACF-106, FAFD-27).
  const flatRateHint = isStandingInvoice(object.billingInterval)
    ? t('objects:hints.flatRateStanding')
    : null;

  const handleSelectActive = async (offerId: string) => {
    if (!objectId) return;
    const ok = await setActiveOffer(objectId, offerId);
    if (ok) {
      setIsPickerOpen(false);
    }
  };

  return (
    <Card title={t('objects:activeOffer.card')}>
      {activeOfferId ? (
        <div className="space-y-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="space-y-1">
              <button
                type="button"
                onClick={() => navigate(`/offers/${activeOfferId}`)}
                className="text-[15px] font-semibold text-[#00A871] hover:underline text-left tabular-nums"
              >
                {offerShown?.offerNumber ?? object.activeOfferNumber ?? activeOfferId}
              </button>
              <div className="flex items-center gap-2 flex-wrap text-[12px] text-slate-500">
                {offerShown?.status && (
                  <Badge variant="success">
                    {t(`offers:status.${offerShown.status.toLowerCase()}`, {
                      defaultValue: offerShown.status,
                    })}
                  </Badge>
                )}
                {offerShown?.offerDate && (
                  <span className="tabular-nums">{formatDateDE(offerShown.offerDate)}</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="secondary"
                onClick={() => navigate(`/offers/${activeOfferId}`)}
              >
                {t('objects:activeOffer.openOffer')}
              </Button>
              {canCreateStanding && (
                <Button
                  variant="secondary"
                  onClick={() => setIsStandingOpen(true)}
                  leftIcon={<CalendarRange className="w-4 h-4" strokeWidth={2} />}
                >
                  {t('invoices:standingDialog.open')}
                </Button>
              )}
              <Button
                onClick={() => setIsAdHocOpen(true)}
                leftIcon={<Plus className="w-4 h-4" strokeWidth={2} />}
              >
                {t('invoices:adHoc.buttonShort')}
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <div className="flex items-center gap-1 text-[11px] font-medium uppercase tracking-wider text-slate-500">
                {t('objects:activeOffer.monthlyTotal')}
                <OfferAmountBreakdown
                  intro={t('objects:activeOffer.breakdown.monthlyIntro')}
                  groups={[{ positions: monthlyPositions }, seasonalGroup]}
                  total={monthlyTotal}
                  formatAmount={formatCurrencyEUR}
                />
              </div>
              <div className="text-[15px] font-semibold text-slate-900 tabular-nums mt-0.5">
                {formatMoney(monthlyTotal)}
              </div>
              {seasonalTotal > 0 && (
                <div className="text-[11px] text-slate-500 mt-0.5 tabular-nums">
                  {t('objects:activeOffer.seasonalExtra', { amount: formatMoney(seasonalTotal) })}
                </div>
              )}
              {flatRateHint && (
                <div className="text-[11px] text-slate-500 mt-0.5">{flatRateHint}</div>
              )}
            </div>
            {offerShown?.totalNetAmount !== undefined && (
              <div>
                <div className="flex items-center gap-1 text-[11px] font-medium uppercase tracking-wider text-slate-500">
                  {t('objects:activeOffer.offerTotal')}
                  <OfferAmountBreakdown
                    intro={t('objects:activeOffer.breakdown.offerIntro')}
                    groups={offerTotalGroups}
                    total={offerShown.totalNetAmount ?? 0}
                    formatAmount={formatCurrencyEUR}
                  />
                </div>
                <div className="text-[15px] font-semibold text-slate-900 tabular-nums mt-0.5">
                  {formatMoney(offerShown.totalNetAmount)}
                </div>
              </div>
            )}
          </div>

          {positionSplit.review.length > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
              {t('offers:billing.reviewHint', { count: positionSplit.review.length })}
            </div>
          )}

          {/* why: a position without a task is never planned — by effort it is never billed. */}
          <NoTaskHint positions={activePositions} />

          {switchableOffers.length > 0 && (
            <button
              type="button"
              onClick={() => setIsPickerOpen(true)}
              className="text-[12px] text-[#00A871] underline hover:opacity-80"
            >
              {t('objects:activeOffer.switch')}
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[13px] font-medium text-slate-900">
                {t('objects:activeOffer.empty')}
              </div>
              <p className="text-[12px] text-slate-500 mt-1">
                {t('objects:activeOffer.emptyHint')}
              </p>
            </div>
            <Button
              onClick={() => setIsAdHocOpen(true)}
              variant="secondary"
              leftIcon={<Plus className="w-4 h-4" strokeWidth={2} />}
            >
              {t('invoices:adHoc.buttonShort')}
            </Button>
          </div>

          {acceptedOffers.length > 0 ? (
            <div className="space-y-1">
              <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
                {t('objects:activeOffer.pickerSubtitle')}
              </div>
              <ul className="space-y-1">
                {acceptedOffers.map((o) => (
                  <li key={o.id} className="flex items-center justify-between">
                    <button
                      type="button"
                      onClick={() => navigate(`/offers/${o.id}`)}
                      className="text-[13px] text-[#00A871] hover:underline tabular-nums"
                    >
                      {o.offerNumber ?? o.id}
                    </button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => o.id && handleSelectActive(o.id)}
                      disabled={saving}
                    >
                      {t('objects:activeOffer.switch')}
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="text-[13px] text-slate-500">
              {t('objects:activeOffer.noAcceptedOffers')}
              <button
                type="button"
                onClick={() =>
                  navigate(`/offers/new${objectId ? `?objectId=${objectId}` : ''}`)
                }
                className="ml-2 text-[#00A871] underline hover:opacity-80"
              >
                {t('objects:activeOffer.createOffer')}
              </button>
            </div>
          )}
        </div>
      )}

      <Modal
        isOpen={isPickerOpen}
        onClose={() => setIsPickerOpen(false)}
        title={t('objects:activeOffer.pickerTitle')}
      >
        <p className="text-[12px] text-slate-500 mb-3">
          {t('objects:activeOffer.pickerSubtitle')}
        </p>
        {switchableOffers.length === 0 ? (
          <div className="text-[13px] text-slate-500">
            {t('objects:activeOffer.noAcceptedOffers')}
          </div>
        ) : (
          <ul className="space-y-2">
            {switchableOffers.map((o) => (
              <li
                key={o.id}
                className="flex items-center justify-between border border-slate-200 rounded-md px-3 py-2"
              >
                <div className="text-[13px] tabular-nums text-slate-700">
                  {o.offerNumber ?? o.id}
                  {o.offerDate && (
                    <span className="ml-2 text-slate-500">
                      ({formatDateDE(o.offerDate)})
                    </span>
                  )}
                </div>
                <Button
                  size="sm"
                  onClick={() => o.id && handleSelectActive(o.id)}
                  disabled={saving}
                >
                  {t('objects:activeOffer.switch')}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Modal>

      <AdHocPlanEntrySlideOver
        isOpen={isAdHocOpen}
        onClose={() => setIsAdHocOpen(false)}
        objectId={objectId ?? null}
      />

      {canCreateStanding && objectId && (
        <StandingInvoiceDialog
          isOpen={isStandingOpen}
          onClose={() => setIsStandingOpen(false)}
          objectId={objectId}
        />
      )}
    </Card>
  );
}
